import React from 'react';
import { toastError, toastSuccess } from 'bench-internal-theme';
import { openModal } from 'modal-core-components';
import { GET_CONTRACTOR_LIST, CREATE_OR_UPDATE_CONTRACTOR } from './graphql';
import { Report1099Fields, Source1099 } from './types';
import AddContractorModal from '../add-contractor-modal/AddContractorModal';
import Report1099Field from '../report-1099-field/Report1099Field';
import { showServerError } from 'bk-web-app';
import { useV2Query, useV2Mutation } from 'utils';

type report1099FormProps = {
  clientId: string;
  fields: {
    contractor: Report1099Fields;
    source: Report1099Fields;
  };
};

const Report1099Form = ({ clientId, fields }: report1099FormProps) => {
  const { data, loading } = useV2Query(GET_CONTRACTOR_LIST, {
    variables: { clientId },
    skip: !clientId,
  });
  const contractor1099Options = data?.contractors?.map(contractorOption => {
    return {
      value: contractorOption.id,
      label: contractorOption.name,
    };
  });

  if (!loading || !contractor1099Options?.length) {
    contractor1099Options?.push(
      {
        value: null,
        label: 'Unknown',
      },
      {
        value: 'Add Contractor',
        label: 'Add Contractor',
      },
    );
  }

  const source1099Options: { value: string | null; label: string }[] = Object.keys(Source1099).map(
    key => {
      return {
        value: key,
        label: Source1099[key],
      };
    },
  );

  source1099Options.push({
    value: null,
    label: 'Unknown',
  });

  const [createOrUpdateContractor] = useV2Mutation(CREATE_OR_UPDATE_CONTRACTOR, {
    onError: error => {
      toastError('An error occurred creating this contractor', showServerError(error));
    },
    onCompleted: response => {
      fields.contractor.onChange('contractor')({
        value: response.createOrUpdateContractor.id,
        label: response.createOrUpdateContractor.name,
      });
      toastSuccess('Contractor Created', 'You have successfully created a contractor.');
    },
  });

  const handleCreateContractor = ({ name }) => {
    if (!clientId) return Promise.resolve();
    return createOrUpdateContractor({
      variables: {
        input: {
          clientId: parseInt(clientId),
          name,
        },
      },
      refetchQueries: [
        {
          query: GET_CONTRACTOR_LIST,
          variables: { clientId },
          context: { useV2: true },
          skip: !clientId,
        },
      ],
    });
  };

  const handleContractorSelect = selectedContractorOption => {
    if (selectedContractorOption?.value === 'Add Contractor') {
      openAddContractorDialog();
    } else {
      fields.contractor.onChange('contractor')(selectedContractorOption);
    }
  };

  const handleSourceSelect = option => {
    fields.source.onChange('source')(option);
  };

  const openAddContractorDialog = () => {
    openModal(closeModal => (
      <AddContractorModal
        closeModal={closeModal}
        title="Add Contractor"
        createContractorData={name => handleCreateContractor({ name })}
      />
    ));
  };

  return (
    <>
      <Report1099Field
        name="Contractor"
        options={contractor1099Options}
        onChange={handleContractorSelect}
        fields={fields.contractor}
        placeholder="Select Contractor..."
      />
      <Report1099Field
        name="Source"
        options={source1099Options}
        onChange={handleSourceSelect}
        fields={fields.source}
        isDisabled={fields.contractor.value === undefined}
        placeholder="Select Source..."
      />
    </>
  );
};

export default Report1099Form;
